'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledDrawer from '@umodeapporg/ui/components/Form/ControlledDrawer';
import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from '@/components/layout/SectionTitle';

import { IAjudaDuvida, IAjudaTela } from '../helpers/ajudaConteudo';
import { ajudaHref } from '../helpers/ajudaNavegacao';
import { DuvidasLista } from './DuvidaItem';

/** A largura do painel. No celular ele ocupa a tela, pela classe abaixo. */
const PAINEL_LARGURA = 420;

/** Quantas dúvidas cabem aqui sem o painel virar o manual: o resto está a um clique. */
const DUVIDAS_NO_PAINEL = 5;

interface IAjudaDaTelaPainelProps {
  tela: IAjudaTela;
  duvidas: Array<IAjudaDuvida>;
  aberto: boolean;
  onClose: () => void;
}

/**
 * A ajuda DESTA tela, ao lado dela.
 *
 * O "?" levava a pessoa para /ajuda, e quem está travado no meio de uma tarefa perde o
 * que estava fazendo para ler a resposta e tem de voltar. Aqui o manual chega por cima,
 * sem tirar ninguém do lugar: as cinco dúvidas da tela, a primeira já aberta, e o caminho
 * para o manual inteiro logo abaixo delas.
 */
export function AjudaDaTelaPainel({ tela, duvidas, aberto, onClose }: IAjudaDaTelaPainelProps) {
  const { t } = useTranslation('common');
  const mostradas = duvidas.slice(0, DUVIDAS_NO_PAINEL);

  return (
    <ControlledDrawer
      open={aberto}
      onClose={onClose}
      placement="right"
      size={PAINEL_LARGURA}
      // No celular a largura fixa deixaria a tela por baixo: lá o painel é a tela inteira.
      className="max-tablet:!w-full"
      unmountOnClose
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">
              {t('ajuda.daTela.titulo')}
            </span>
            <SectionTitle size="md" className="truncate">
              {tela.nome}
            </SectionTitle>
            <p className="text-sm text-foreground-muted">{tela.paraQue}</p>
          </div>
          <ControlledTooltip content={t('actions.close')} className="z-[9999]">
            <ControlledButton
              type="button"
              variant="text"
              tone="neutral"
              size="sm"
              aria-label={t('actions.close')}
              onClick={onClose}
            >
              <MaterialSymbol icon="close" size={18} />
            </ControlledButton>
          </ControlledTooltip>
        </div>

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
          <DuvidasLista duvidas={mostradas} inicial={mostradas[0]?.id} />

          {/* Logo abaixo das dúvidas, e não num rodapé fixo: o rodapé do painel cai em
              cima dos botões que moram no canto da tela. */}
          <LinkButton
            href={ajudaHref({ tela: tela.id })}
            variant="text"
            tone="brand"
            size="sm"
            className="self-start normal-case"
          >
            {t('ajuda.daTela.verTudo')}
            <MaterialSymbol icon="arrow_forward" size={16} className="ml-1.5 shrink-0" aria-hidden />
          </LinkButton>
        </div>
      </div>
    </ControlledDrawer>
  );
}
